import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';

const CATEGORIES = ['All', 'Email Automation', 'Data Sync', 'AI Pipelines', 'DevOps', 'Productivity'];

const CATEGORY_COLORS = {
  'Email Automation': '#e8a838',
  'Data Sync': '#4a9eff',
  'AI Pipelines': '#a855f7',
  'DevOps': '#ef4444',
  'Productivity': '#22c55e',
};

export default function WorkflowTemplatesModal({ onClose, onSelectTemplate }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/templates`)
      .then(res => { setTemplates(res.data); setLoading(false); })
      .catch(() => { setError('Failed to load templates.'); setLoading(false); });
  }, []);

  const filtered = useMemo(() => {
    let list = templates;
    if (activeCategory !== 'All') {
      list = list.filter(t => t.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        (t.tags || []).some(tag => tag.toLowerCase().includes(q))
      );
    }
    return list;
  }, [templates, activeCategory, search]);

  const handleUseTemplate = async (templateId) => {
    setImporting(templateId);
    try {
      const res = await axios.get(`${API_BASE_URL}/templates/${templateId}`);
      onSelectTemplate(res.data);
    } catch {
      alert('Failed to load template. Please try again.');
    } finally {
      setImporting(null);
    }
  };

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalStyle = {
    background: 'var(--bg-primary)',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
    width: '880px',
    maxWidth: '95vw',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const headerStyle = {
    padding: '24px 28px 0',
    borderBottom: '1px solid var(--border-color)',
  };

  const titleStyle = {
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: 0,
  };

  const subtitleStyle = {
    fontSize: '13px',
    color: 'var(--text-tertiary)',
    marginTop: '4px',
    marginBottom: '16px',
  };

  const searchStyle = {
    width: '100%',
    padding: '8px 12px',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '16px',
  };

  const tabsStyle = {
    display: 'flex',
    gap: '4px',
    overflowX: 'auto',
    paddingBottom: '0',
  };

  const tabStyle = (active) => ({
    padding: '8px 14px',
    borderRadius: '6px 6px 0 0',
    border: 'none',
    background: active ? 'var(--bg-secondary)' : 'transparent',
    color: active ? 'var(--accent-color)' : 'var(--text-secondary)',
    fontWeight: active ? 600 : 400,
    fontSize: '13px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    borderBottom: active ? '2px solid var(--accent-color)' : '2px solid transparent',
  });

  const bodyStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 28px',
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '16px',
  };

  const cardStyle = {
    background: 'var(--bg-node)',
    border: '1px solid var(--border-color)',
    borderRadius: '10px',
    padding: '18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    transition: 'border-color 0.15s',
  };

  const cardIconStyle = {
    fontSize: '28px',
    lineHeight: 1,
  };

  const cardNameStyle = {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
  };

  const cardDescStyle = {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
    margin: 0,
    flexGrow: 1,
  };

  const categoryBadgeStyle = (category) => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
    background: (CATEGORY_COLORS[category] || '#888') + '22',
    color: CATEGORY_COLORS[category] || '#888',
  });

  const credsBadgesStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  };

  const credBadgeStyle = {
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    background: 'var(--bg-secondary)',
    color: 'var(--text-tertiary)',
    border: '1px solid var(--border-color)',
  };

  const useButtonStyle = (isImporting) => ({
    padding: '7px 14px',
    background: isImporting ? 'var(--border-color)' : 'var(--accent-color)',
    color: isImporting ? 'var(--text-secondary)' : '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: isImporting ? 'not-allowed' : 'pointer',
    width: '100%',
    marginTop: '4px',
  });

  const closeBtnStyle = {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-secondary)',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
  };

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={titleStyle}>📋 Workflow Templates</h2>
              <p style={subtitleStyle}>Start from a ready-made template and customize credentials and parameters.</p>
            </div>
            <button style={closeBtnStyle} onClick={onClose} title="Close">✕</button>
          </div>
          <input
            style={searchStyle}
            placeholder="Search templates by name, description, or tag…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
          <div style={tabsStyle}>
            {CATEGORIES.map(cat => (
              <button key={cat} style={tabStyle(activeCategory === cat)} onClick={() => setActiveCategory(cat)}>
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div style={bodyStyle}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              Loading templates…
            </div>
          )}
          {error && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--error-color)' }}>
              {error}
            </div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              No templates found{search ? ` for "${search}"` : ''}.
            </div>
          )}
          {!loading && !error && filtered.length > 0 && (
            <div style={gridStyle}>
              {filtered.map(template => {
                const isImporting = importing === template.id;
                return (
                  <div key={template.id} style={cardStyle}>
                    <div style={cardIconStyle}>{template.icon || '⚙️'}</div>
                    <p style={cardNameStyle}>{template.name}</p>
                    <p style={cardDescStyle}>{template.description}</p>
                    <div>
                      <span style={categoryBadgeStyle(template.category)}>{template.category}</span>
                    </div>
                    {template.required_credentials?.length > 0 && (
                      <div style={credsBadgesStyle}>
                        {template.required_credentials.map(cred => (
                          <span key={cred} style={credBadgeStyle}>{cred}</span>
                        ))}
                      </div>
                    )}
                    <button
                      style={useButtonStyle(isImporting)}
                      disabled={isImporting}
                      onClick={() => handleUseTemplate(template.id)}
                    >
                      {isImporting ? 'Loading…' : 'Use Template'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
